/**
 * planDiff — item-keyed diff of plan-of-record snapshot payloads.
 *
 * THE defect this replaces (PlanningChanges + MobileChanges carried duplicate
 * copies): the old diff keyed initiatives on payload `id` — the initiatives
 * ROW id, which regenerates on every plan version — so two snapshots of an
 * UNCHANGED plan reported every initiative as both added and removed. The
 * durable identity across versions is `item_id` (the same key
 * plan_commitment_terms / plan_resource_claims / plan_item_placements use);
 * the SQL twin of this diff is the plan_snapshot_deltas view (mig 683).
 *
 * Pure module: no React, no Supabase. Legacy payloads that predate item_id
 * fall back to row id — which preserves the old (broken) behavior for old
 * snapshots rather than inventing identities we don't have (L1).
 */

export interface DiffMetric {
  id: string;
  label: string;
  value: number;
}

interface InitiativeLike {
  id?: string | null;
  item_id?: string | null;
}

interface WorkUnitLike {
  id?: string | null;
  title?: string | null;
}

interface MilestoneLike {
  id?: string | null;
  title?: string | null;
}

interface AssumptionLike {
  id?: string | null;
  statement?: string | null;
  status?: string | null;
}

export interface PlanPayloadLike {
  initiatives?: InitiativeLike[] | null;
  work_units?: WorkUnitLike[] | null;
  milestones?: MilestoneLike[] | null;
  assumptions?: AssumptionLike[] | null;
}

function arr<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

/** Durable identity: item_id when present; legacy row id otherwise. */
function initiativeKey(i: InitiativeLike): string {
  return i.item_id ?? i.id ?? '';
}

/** Work units / milestones have no durable id across versions — title is the
 * most stable identity the payload carries. */
function titleKey(x: { id?: string | null; title?: string | null }): string {
  return (x.title ?? '').trim().toLowerCase() || (x.id ?? '');
}

function keySet<T>(items: T[], key: (t: T) => string): Set<string> {
  const s = new Set<string>();
  for (const i of items) {
    const k = key(i);
    if (k) s.add(k);
  }
  return s;
}

function countMissing(a: Set<string>, b: Set<string>): number {
  let n = 0;
  for (const k of a) if (!b.has(k)) n += 1;
  return n;
}

export function buildPlanDiffMetrics(
  latest: PlanPayloadLike | null | undefined,
  previous: PlanPayloadLike | null | undefined
): DiffMetric[] {
  const li = keySet(arr(latest?.initiatives), initiativeKey);
  const pi = keySet(arr(previous?.initiatives), initiativeKey);
  const lw = keySet(arr(latest?.work_units), titleKey);
  const pw = keySet(arr(previous?.work_units), titleKey);
  const lm = keySet(arr(latest?.milestones), titleKey);
  const pm = keySet(arr(previous?.milestones), titleKey);

  // Assumptions: a DELTA of invalidations — invalidated now, not invalidated
  // before (the old code counted the absolute current number and never read
  // `previous` at all).
  const previouslyInvalidated = new Set(
    arr(previous?.assumptions)
      .filter((a) => a.status === 'invalidated')
      .map((a) => (a.statement ?? a.id ?? '').trim())
  );
  const newlyInvalidated = arr(latest?.assumptions).filter(
    (a) =>
      a.status === 'invalidated' &&
      !previouslyInvalidated.has((a.statement ?? a.id ?? '').trim())
  ).length;

  return [
    { id: 'initiatives_added', label: 'Initiatives added', value: countMissing(li, pi) },
    { id: 'initiatives_removed', label: 'Initiatives removed', value: countMissing(pi, li) },
    { id: 'work_units_added', label: 'Work units added', value: countMissing(lw, pw) },
    { id: 'work_units_removed', label: 'Work units removed', value: countMissing(pw, lw) },
    { id: 'milestones_added', label: 'Milestones added', value: countMissing(lm, pm) },
    { id: 'assumptions_invalidated', label: 'Assumptions newly invalidated', value: newlyInvalidated },
  ];
}
